import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GameProps, GameDef, playAreaStyle } from './types';

// ── CATCH THE FALLING ────────────────────────────────────────────────────────
// Goodies fall from the top; drag a basket left/right to catch fruit and dodge
// bombs. Catching fruit scores; a bomb ends the run early. 18s.

const GAME_SECONDS = 18;
const FRUIT = ['🍎', '🍊', '🍓', '🍌', '🍇', '🍒', '🍑', '🥝'];
const BOMB = '💣';
const FALL_TICK = 28; // ms per physics step
const SPAWN_INTERVAL = 850; // ms between drops
const BASKET_W = 18; // % width
const CATCH_Y = 84; // basket centerline (% of height)

interface Item {
  id: number;
  emoji: string;
  isBomb: boolean;
  x: number; // %
  y: number; // %
  vy: number; // % per tick
}

function CatchFalling({ theme, isKid, onEnd }: GameProps) {
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [score, setScore] = useState(0);
  const [items, setItems] = useState<Item[]>([]);
  const [basketX, setBasketX] = useState(50);
  const [flash, setFlash] = useState<string | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(0);
  const scoreRef = useRef(0);
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onEnd({ score: scoreRef.current, noun: scoreRef.current === 1 ? 'treat' : 'treats' });
  }, [onEnd]);

  // countdown
  useEffect(() => {
    if (doneRef.current) return;
    if (timeLeft <= 0) { finish(); return; }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, finish]);

  // spawn drops
  useEffect(() => {
    const spawn = setInterval(() => {
      const isBomb = Math.random() < 0.22;
      setItems(prev => [
        ...prev,
        {
          id: nextId.current++,
          emoji: isBomb ? BOMB : FRUIT[Math.floor(Math.random() * FRUIT.length)],
          isBomb,
          x: 8 + Math.random() * 84,
          y: -6,
          vy: 1.1 + Math.random() * 0.7,
        },
      ]);
    }, SPAWN_INTERVAL);
    return () => clearInterval(spawn);
  }, []);

  // physics + catch detection
  useEffect(() => {
    const tick = setInterval(() => {
      setItems(prev => {
        const next: Item[] = [];
        for (const it of prev) {
          const y = it.y + it.vy;
          // within catch band and over the basket?
          if (y >= CATCH_Y - 4 && y <= CATCH_Y + 6 && Math.abs(it.x - basketX) <= BASKET_W / 2) {
            if (it.isBomb) {
              setFlash('💥');
              setTimeout(finish, 350);
            } else {
              scoreRef.current += 1;
              setScore(scoreRef.current);
              setFlash('✨');
              setTimeout(() => setFlash(null), 250);
            }
            continue; // consumed
          }
          if (y > 100) continue; // missed, off-screen
          next.push({ ...it, y });
        }
        return next;
      });
    }, FALL_TICK);
    return () => clearInterval(tick);
  }, [basketX, finish]);

  // drag / pointer move
  const moveBasket = useCallback((clientX: number) => {
    const el = areaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setBasketX(Math.max(BASKET_W / 2, Math.min(100 - BASKET_W / 2, pct)));
  }, []);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 800, fontSize: isKid ? '1.3rem' : '1.1rem',
      }}>
        <span>🧺 {score}</span>
        <span style={{ color: timeLeft <= 5 ? '#ef4444' : theme.accent }}>⏱️ {timeLeft}s</span>
      </div>

      <div
        ref={areaRef}
        onPointerMove={(e) => moveBasket(e.clientX)}
        onPointerDown={(e) => moveBasket(e.clientX)}
        style={{ ...playAreaStyle(theme), cursor: 'ew-resize' }}
      >
        {items.map(it => (
          <span
            key={it.id}
            style={{
              position: 'absolute',
              left: `${it.x}%`,
              top: `${it.y}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: isKid ? '2.4rem' : '2rem',
              pointerEvents: 'none',
              lineHeight: 1,
            }}
          >
            {it.emoji}
          </span>
        ))}

        {flash && (
          <span style={{
            position: 'absolute', left: `${basketX}%`, top: `${CATCH_Y - 6}%`,
            transform: 'translate(-50%, -50%)', fontSize: '2rem', pointerEvents: 'none',
            animation: 'reward-burst 0.4s ease-out forwards',
          }}>{flash}</span>
        )}

        {/* basket */}
        <span style={{
          position: 'absolute',
          left: `${basketX}%`,
          top: `${CATCH_Y}%`,
          transform: 'translate(-50%, -50%)',
          fontSize: isKid ? '3rem' : '2.6rem',
          pointerEvents: 'none',
          lineHeight: 1,
          filter: `drop-shadow(0 2px 3px ${theme.accent}66)`,
        }}>🧺</span>
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.8rem', marginTop: '6px', opacity: 0.7 }}>
        drag to move the basket • catch fruit, dodge 💣
      </div>
    </>
  );
}

export const CATCH_FALLING: GameDef = {
  id: 'catch-falling',
  title: 'Catch It!',
  emoji: '🧺',
  instructions: 'Drag the basket to catch falling fruit — but dodge the bombs!',
  verb: 'Catch',
  Component: CatchFalling,
};

export default CatchFalling;
